import { createFileRoute } from "@tanstack/react-router";
import React from "react";
import { useTranslation } from "react-i18next";
import { ApiInternal } from "src/api/api_internal";
import { useForm, useStore } from "@tanstack/react-form";
import { isFormError } from "src/utils/form-error";
import { notify } from "src/components/base/toast";
import Form from "src/components/base/form";
import {
    Description,
    ErrorMessage,
    Field,
    FieldGroup,
    Fieldset,
    Label,
    RequiredLabel,
} from "src/components/base/fieldset";
import { Input } from "src/components/base/input";
import { Button, PrimaryButton } from "src/components/base/button";
import BlockUnsavedChanges from "src/components/base/unsaved-changes";
import { Divider } from "src/components/base/divider";
import SINGLE_TENANT_CONTEXT from "src/context/internal/tenant";
import { BadgeColor } from "src/api/generated/frontend_internal_v1";
import ColorPicker, { type BadgeColorKey } from "src/components/base/color-picker";
import { TO_BADGE_KEY } from "src/components/badges/tenant";
import { Badge } from "src/components/base/badge";
import { BuildingOffice2Icon, ChevronDownIcon } from "@heroicons/react/20/solid";

/** Inverse of {@link TO_BADGE_KEY} */
const TO_BADGE_COLOR = Object.fromEntries(Object.entries(TO_BADGE_KEY).map(([color, key]) => [key, color])) as Record<
    BadgeColorKey,
    BadgeColor
>;

/**
 * The properties for {@link TenantGeneralView}
 */
export type TenantGeneralViewProps = {};

/**
 * The general view for a tenant
 */
export default function TenantGeneralView(props: TenantGeneralViewProps) {
    const [t] = useTranslation("tenant");
    const { tenant, reset } = React.useContext(SINGLE_TENANT_CONTEXT);

    const form = useForm({
        defaultValues: {
            name: tenant.name,
            description: tenant.description,
            badge_name: tenant.badge_name,
            badge_color: tenant.badge_color,
        },
        validators: {
            onSubmitAsync: async ({ value }) => {
                const res = await ApiInternal.tenants.update(tenant.uuid, {
                    name: value.name,
                    description: value.description,
                    badge_name: value.badge_name,
                    badge_color: value.badge_color,
                });
                if (isFormError(res)) {
                    return {
                        fields: {
                            name: res.error.name_already_used ? t("error.name-already-used") : undefined,
                        },
                    };
                }
                notify.success(t("toast.successfully-updated"));
                form.reset();
                await reset();
            },
        },
    });
    const isDirty = useStore(form.store, (store) => store.isDirty);

    return (
        <div className={"flex flex-col gap-6"}>
            <Form onSubmit={form.handleSubmit} className={"flex max-w-lg flex-col gap-6"}>
                <Fieldset>
                    <FieldGroup>
                        <form.Field name={"name"}>
                            {(fieldApi) => (
                                <Field>
                                    <RequiredLabel>{t("label.name")}</RequiredLabel>
                                    <Description>{t("description.name")}</Description>
                                    <Input
                                        autoFocus={true}
                                        required={true}
                                        invalid={fieldApi.state.meta.errors.length > 0}
                                        value={fieldApi.state.value}
                                        maxLength={255}
                                        onChange={(e) => fieldApi.handleChange(e.target.value)}
                                    />
                                    {fieldApi.state.meta.errors.map((err) => (
                                        <ErrorMessage>{err}</ErrorMessage>
                                    ))}
                                </Field>
                            )}
                        </form.Field>

                        <form.Field name={"description"}>
                            {(fieldApi) => (
                                <Field>
                                    <Label>{t("label.description")}</Label>
                                    <Description>{t("description.description")}</Description>
                                    <Input
                                        autoFocus={true}
                                        invalid={fieldApi.state.meta.errors.length > 0}
                                        value={fieldApi.state.value}
                                        maxLength={1024}
                                        onChange={(e) => fieldApi.handleChange(e.target.value)}
                                    />
                                    {fieldApi.state.meta.errors.map((err) => (
                                        <ErrorMessage>{err}</ErrorMessage>
                                    ))}
                                </Field>
                            )}
                        </form.Field>

                        <form.Field name={"badge_name"}>
                            {(fieldApi) => (
                                <Field>
                                    <Label>{t("label.badge-name")}</Label>
                                    <Description>{t("description.badge-name")}</Description>
                                    <Input
                                        required={true}
                                        invalid={fieldApi.state.meta.errors.length > 0}
                                        value={fieldApi.state.value}
                                        maxLength={255}
                                        onChange={(e) => fieldApi.handleChange(e.target.value)}
                                    />
                                    {fieldApi.state.meta.errors.map((err) => (
                                        <ErrorMessage>{err}</ErrorMessage>
                                    ))}
                                </Field>
                            )}
                        </form.Field>

                        <form.Field name={"badge_color"}>
                            {(fieldApi) => (
                                <Field>
                                    <Label>{t("label.badge-color")}</Label>
                                    <div className={"flex items-center justify-between gap-12"}>
                                        <Description>{t("description.badge-color")}</Description>
                                        <form.Subscribe selector={(state) => state.values.badge_name}>
                                            {(badgeName) => (
                                                <ColorPicker
                                                    value={TO_BADGE_KEY[fieldApi.state.value]}
                                                    onChange={(v) => fieldApi.handleChange(TO_BADGE_COLOR[v])}
                                                    renderValue={(color) => (
                                                        <Badge className={"h-fit"} color={color}>
                                                            <BuildingOffice2Icon className={"size-4 shrink-0"} />
                                                            <span className={"truncate"}>{badgeName}</span>
                                                            <span
                                                                className={
                                                                    "-my-0.5 self-stretch border-l border-zinc-950/10 dark:border-white/10"
                                                                }
                                                            />
                                                            <ChevronDownIcon className={"size-4 shrink-0"} />
                                                        </Badge>
                                                    )}
                                                />
                                            )}
                                        </form.Subscribe>
                                    </div>
                                    {fieldApi.state.meta.errors.map((err) => (
                                        <ErrorMessage>{err}</ErrorMessage>
                                    ))}
                                </Field>
                            )}
                        </form.Field>
                    </FieldGroup>
                </Fieldset>

                <Divider />
                <div className={"flex justify-end gap-3"}>
                    <Button plain={true} disabled={!isDirty} onClick={() => form.reset()}>
                        {t("button.reset")}
                    </Button>
                    <PrimaryButton type="submit" disabled={!isDirty}>
                        {t("button.save")}
                    </PrimaryButton>
                </div>
                <BlockUnsavedChanges condition={isDirty} />
            </Form>
        </div>
    );
}

export const Route = createFileRoute("/_admin/a/tenants/$tenantId/_tenant/general")({
    component: TenantGeneralView,
});
